import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Polyline, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
    AlertTriangle,
    Award,
    CalendarClock,
    CheckCircle2,
    Crosshair,
    MapPin,
    Ruler,
    Target,
    XCircle,
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { Profile } from '../../../types';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { haversineKm } from '../../../src/lib/routeOptimizer';

interface ParadaAder {
    id: string;
    status: string;
    posicao: number;
    visitado_em?: string | null;
    checkin_lat?: number | null;
    checkin_lng?: number | null;
    foto_url?: string | null;
    negociacao_id?: number | null;
    negociacoes?: {
        titulo?: string | null;
        cidade?: string | null;
        bairro?: string | null;
        latitude?: number | null;
        longitude?: number | null;
    } | null;
}

interface RotaAder {
    id: string;
    data: string;
    status: string;
    nome?: string | null;
    vendedor_id: string;
    rota_paradas: ParadaAder[];
}

interface Comparado {
    paradaId: string;
    titulo: string;
    cidade?: string | null;
    plan: [number, number];
    exec: [number, number];
    desvioM: number;
    aderente: boolean;
    vendedorId: string;
    data: string;
    rotaNome?: string | null;
    visitadoEm?: string | null;
    fotoUrl?: string | null;
}

interface NaoVisitado {
    paradaId: string;
    titulo: string;
    cidade?: string | null;
    plan: [number, number] | null;
    vendedorId: string;
    data: string;
    rotaNome?: string | null;
}

type Periodo = 'hoje' | 'ontem' | '7d' | '14d' | '30d' | '90d' | 'custom';

const PERIODOS: Array<{ value: Periodo; label: string }> = [
    { value: 'hoje', label: 'Hoje' },
    { value: 'ontem', label: 'Ontem' },
    { value: '7d', label: '7 dias' },
    { value: '14d', label: '14 dias' },
    { value: '30d', label: '30 dias' },
    { value: '90d', label: '90 dias' },
    { value: 'custom', label: 'Personalizado' },
];

const RAIOS = [50, 100, 150, 300, 500]; // metros — limite de aderência ao endereço planejado

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const rangeDoPeriodo = (p: Periodo, customIni: string, customFim: string): [string, string] => {
    const hoje = new Date();
    switch (p) {
        case 'hoje': return [iso(hoje), iso(hoje)];
        case 'ontem': { const d = new Date(hoje); d.setDate(d.getDate() - 1); return [iso(d), iso(d)]; }
        case '7d': { const d = new Date(hoje); d.setDate(d.getDate() - 6); return [iso(d), iso(hoje)]; }
        case '14d': { const d = new Date(hoje); d.setDate(d.getDate() - 13); return [iso(d), iso(hoje)]; }
        case '30d': { const d = new Date(hoje); d.setDate(d.getDate() - 29); return [iso(d), iso(hoje)]; }
        case '90d': { const d = new Date(hoje); d.setDate(d.getDate() - 89); return [iso(d), iso(hoje)]; }
        case 'custom': return [customIni || iso(hoje), customFim || iso(hoje)];
    }
};

const fmtNum = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
const fmtPct = (v: number) => `${v.toFixed(1)}%`;
const fmtDist = (m: number) => m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;

const Kpi: React.FC<{ label: string; value: string; icon: React.ReactNode; tone?: string; hint?: string }> = ({ label, value, icon, tone = 'text-gray-900 dark:text-white', hint }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4" title={hint}>
        <div className="flex items-start justify-between">
            <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
            <span className="text-gray-300 dark:text-gray-600">{icon}</span>
        </div>
        <p className={`text-xl font-bold mt-1 ${tone}`}>{value}</p>
    </div>
);

/** Ajusta o enquadramento e corrige o tamanho do canvas ao montar em aba recém-exibida. */
const FitBounds: React.FC<{ coords: Array<[number, number]> }> = ({ coords }) => {
    const map = useMap();
    const chave = coords.length;
    useEffect(() => {
        const t = setTimeout(() => {
            map.invalidateSize();
            if (coords.length === 0) return;
            map.fitBounds(L.latLngBounds(coords).pad(0.25), { maxZoom: 14 });
        }, 150);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chave, map]);
    return null;
};

interface AderenciaPanelProps {
    profile: Profile | null;
    profiles: Profile[];
    vendedorSel: string; // '' = toda a equipe
}

/**
 * Comparativo Planejado × Executado (Motoboy M3): cruza a coordenada do lead (planejado)
 * com o local do check-in (executado) para medir aderência, desvio, visitas fora de rota
 * e endereços não visitados.
 */
const AderenciaPanel: React.FC<AderenciaPanelProps> = ({ profile, profiles, vendedorSel }) => {
    const [periodo, setPeriodo] = useState<Periodo>('30d');
    const [customIni, setCustomIni] = useState(iso(new Date()));
    const [customFim, setCustomFim] = useState(iso(new Date()));
    const [raio, setRaio] = useState(150);
    const [rotas, setRotas] = useState<RotaAder[]>([]);
    const [loading, setLoading] = useState(true);
    const [destaque, setDestaque] = useState<string | null>(null);

    const [ini, fim] = rangeDoPeriodo(periodo, customIni, customFim);
    const hojeIso = iso(new Date());

    useEffect(() => {
        const fetchRotas = async () => {
            if (!profile) return;
            setLoading(true);
            try {
                let query = supabase
                    .from('rotas_comerciais')
                    .select('id, data, status, nome, vendedor_id, rota_paradas(id, status, posicao, visitado_em, checkin_lat, checkin_lng, foto_url, negociacao_id, negociacoes(titulo, cidade, bairro, latitude, longitude))')
                    .gte('data', ini)
                    .lte('data', fim)
                    .neq('status', 'cancelada');
                if (vendedorSel) query = query.eq('vendedor_id', vendedorSel);
                const { data, error } = await query;
                if (error) throw error;
                setRotas((data || []) as unknown as RotaAder[]);
            } catch (err) {
                console.error('Erro ao carregar aderência:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchRotas();
    }, [profile?.id, vendedorSel, ini, fim]);

    const nome = (id?: string | null) => profiles.find(p => String(p.id) === String(id))?.full_name?.split(' ').slice(0, 2).join(' ') || '—';

    const dados = useMemo(() => {
        const comparados: Comparado[] = [];
        const naoVisitados: NaoVisitado[] = [];
        let visitadasSemGeo = 0; // executadas sem coordenada do plano ou do check-in (não mensuráveis)
        const porOperador: Record<string, { comparadas: number; aderentes: number; desvioSum: number; naoVisitados: number }> = {};
        const op = (id: string) => (porOperador[id] ||= { comparadas: 0, aderentes: 0, desvioSum: 0, naoVisitados: 0 });

        for (const r of rotas) {
            // Rota "vencida": já passou ou foi concluída — só aí faz sentido cobrar o que não foi visitado
            const vencida = r.data < hojeIso || r.status === 'concluida';
            for (const p of r.rota_paradas) {
                const neg = p.negociacoes;
                const plan: [number, number] | null = (neg?.latitude != null && neg?.longitude != null)
                    ? [Number(neg.latitude), Number(neg.longitude)] : null;
                const exec: [number, number] | null = (p.checkin_lat != null && p.checkin_lng != null)
                    ? [Number(p.checkin_lat), Number(p.checkin_lng)] : null;
                const titulo = neg?.titulo || 'Lead';

                if (p.status === 'visitado') {
                    if (plan && exec) {
                        const desvioM = haversineKm({ lat: plan[0], lng: plan[1] }, { lat: exec[0], lng: exec[1] }) * 1000;
                        const aderente = desvioM <= raio;
                        comparados.push({
                            paradaId: p.id, titulo, cidade: neg?.cidade, plan, exec, desvioM, aderente,
                            vendedorId: r.vendedor_id, data: r.data, rotaNome: r.nome, visitadoEm: p.visitado_em, fotoUrl: p.foto_url,
                        });
                        const o = op(r.vendedor_id);
                        o.comparadas++; o.desvioSum += desvioM; if (aderente) o.aderentes++;
                    } else {
                        visitadasSemGeo++;
                    }
                } else if (vencida) {
                    naoVisitados.push({
                        paradaId: p.id, titulo, cidade: neg?.cidade, plan,
                        vendedorId: r.vendedor_id, data: r.data, rotaNome: r.nome,
                    });
                    op(r.vendedor_id).naoVisitados++;
                }
            }
        }

        const aderentes = comparados.filter(c => c.aderente).length;
        const fora = comparados.filter(c => !c.aderente).sort((a, b) => b.desvioM - a.desvioM);
        const taxaAderencia = comparados.length > 0 ? (aderentes / comparados.length) * 100 : 0;
        const desvioMedio = comparados.length > 0 ? comparados.reduce((s, c) => s + c.desvioM, 0) / comparados.length : 0;
        const planejadasTotal = comparados.length + visitadasSemGeo + naoVisitados.length;
        const eficiencia = planejadasTotal > 0 ? ((comparados.length + visitadasSemGeo) / planejadasTotal) * 100 : 0;

        const ranking = Object.entries(porOperador)
            .map(([id, v]) => ({
                id,
                comparadas: v.comparadas,
                aderentes: v.aderentes,
                naoVisitados: v.naoVisitados,
                taxa: v.comparadas > 0 ? (v.aderentes / v.comparadas) * 100 : 0,
                desvioMedio: v.comparadas > 0 ? v.desvioSum / v.comparadas : 0,
            }))
            .filter(v => v.comparadas > 0 || v.naoVisitados > 0)
            .sort((a, b) => b.taxa - a.taxa || b.comparadas - a.comparadas);

        return {
            comparados, fora, naoVisitados, visitadasSemGeo, aderentes,
            taxaAderencia, desvioMedio, eficiencia, ranking,
        };
    }, [rotas, raio, hojeIso]);

    // Coordenadas para enquadrar o mapa (planejado + executado + planejado não visitado)
    const coordsMapa = useMemo(() => {
        const cs: Array<[number, number]> = [];
        for (const c of dados.comparados) { cs.push(c.plan); cs.push(c.exec); }
        for (const n of dados.naoVisitados) if (n.plan) cs.push(n.plan);
        return cs;
    }, [dados]);

    const mapsLink = (a: [number, number], b: [number, number]) =>
        `https://www.google.com/maps/dir/?api=1&origin=${a[0]},${a[1]}&destination=${b[0]},${b[1]}&travelmode=driving`;

    return (
        <div className="space-y-4">
            {/* Filtros */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 flex flex-wrap items-center gap-2">
                <Crosshair size={15} className="text-gray-400 ml-1" />
                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 mr-1">
                    Planejado × Executado {vendedorSel ? `· ${nome(vendedorSel)}` : '· Toda a Equipe'}
                </span>
                <div className="flex items-center gap-1 flex-wrap ml-auto">
                    <span className="flex items-center gap-1 mr-1" title="Distância máxima do check-in ao endereço planejado para a visita contar como aderente">
                        <Ruler size={13} className="text-gray-400" />
                        <select
                            value={raio}
                            onChange={e => setRaio(Number(e.target.value))}
                            className="text-xs font-medium px-2.5 py-1.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-none focus:outline-none"
                        >
                            {RAIOS.map(r => <option key={r} value={r}>± {r} m</option>)}
                        </select>
                    </span>
                    {PERIODOS.map(p => (
                        <button
                            key={p.value}
                            onClick={() => setPeriodo(p.value)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${periodo === p.value
                                ? 'bg-primary text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                        >
                            {p.label}
                        </button>
                    ))}
                    {periodo === 'custom' && (
                        <span className="flex items-center gap-1.5 ml-1">
                            <input type="date" value={customIni} onChange={e => setCustomIni(e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none" />
                            <span className="text-xs text-gray-400">→</span>
                            <input type="date" value={customFim} onChange={e => setCustomFim(e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none" />
                        </span>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><LoadingSpinner label="Cruzando planejado × executado..." size="sm" /></div>
            ) : (
                <>
                    {/* KPIs */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <Kpi
                            label="Taxa de Aderência"
                            value={dados.comparados.length > 0 ? fmtPct(dados.taxaAderencia) : '—'}
                            icon={<Target size={18} />}
                            tone={dados.taxaAderencia >= 80 ? 'text-green-600 dark:text-green-400' : dados.taxaAderencia >= 50 ? 'text-amber-500' : 'text-red-500'}
                            hint={`Check-ins dentro de ${raio} m do endereço planejado ÷ visitas mensuráveis`}
                        />
                        <Kpi
                            label="Desvio Médio"
                            value={dados.comparados.length > 0 ? fmtDist(dados.desvioMedio) : '—'}
                            icon={<Ruler size={18} />}
                            tone="text-blue-600 dark:text-blue-400"
                            hint="Distância média entre o check-in e o endereço planejado"
                        />
                        <Kpi
                            label="Visitas Fora de Rota"
                            value={fmtNum(dados.fora.length)}
                            icon={<AlertTriangle size={18} />}
                            tone={dados.fora.length === 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}
                            hint={`Check-in a mais de ${raio} m do endereço planejado`}
                        />
                        <Kpi
                            label="Endereços Não Visitados"
                            value={fmtNum(dados.naoVisitados.length)}
                            icon={<XCircle size={18} />}
                            tone={dados.naoVisitados.length === 0 ? 'text-green-600 dark:text-green-400' : 'text-amber-500'}
                            hint="Paradas planejadas em rotas vencidas/concluídas sem check-in"
                        />
                    </div>

                    {/* Chips de contexto */}
                    <div className="flex flex-wrap gap-2">
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Visitas mensuradas: <b>{fmtNum(dados.comparados.length)}</b></span>
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Aderentes: <b>{fmtNum(dados.aderentes)}</b></span>
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">Eficiência operacional: <b>{fmtPct(dados.eficiencia)}</b></span>
                        {dados.visitadasSemGeo > 0 && (
                            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" title="Visitas sem coordenada do lead ou sem geolocalização no check-in — não dá para medir o desvio">Sem georreferência: <b>{fmtNum(dados.visitadasSemGeo)}</b></span>
                        )}
                    </div>

                    {/* Mapa planejado → executado */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            <div>
                                <h2 className="text-sm font-bold text-gray-900 dark:text-white">Mapa Planejado × Executado</h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400">A linha liga o endereço planejado ao local real do check-in</p>
                            </div>
                            <div className="flex items-center gap-3 text-[11px] font-semibold">
                                <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400"><span className="w-2.5 h-2.5 rounded-full border-2 border-blue-500"></span> Planejado</span>
                                <span className="flex items-center gap-1 text-green-600 dark:text-green-400"><span className="w-2.5 h-2.5 rounded-full bg-green-500"></span> Aderente</span>
                                <span className="flex items-center gap-1 text-red-500"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span> Fora de rota</span>
                                <span className="flex items-center gap-1 text-amber-500"><span className="w-2.5 h-2.5 rounded-full bg-amber-400"></span> Não visitado</span>
                            </div>
                        </div>

                        <div className="h-[440px] rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-inner relative z-0">
                            {coordsMapa.length === 0 ? (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-800 text-gray-400">
                                    <Crosshair size={36} className="mb-2 opacity-30" />
                                    <p className="text-sm">Nenhuma visita georreferenciada no período.</p>
                                </div>
                            ) : (
                                <MapContainer center={[-16.68, -49.25]} zoom={7} scrollWheelZoom className="h-full w-full">
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <FitBounds coords={coordsMapa} />

                                    {/* Linha planejado → executado */}
                                    {dados.comparados.map(c => {
                                        const cor = c.aderente ? '#10b981' : '#ef4444';
                                        const realce = destaque === c.paradaId;
                                        return (
                                            <React.Fragment key={c.paradaId}>
                                                <Polyline
                                                    positions={[c.plan, c.exec]}
                                                    pathOptions={{ color: cor, weight: realce ? 5 : 2.5, opacity: realce ? 1 : 0.65, dashArray: c.aderente ? undefined : '5 6' }}
                                                />
                                                {/* Planejado */}
                                                <CircleMarker center={c.plan} radius={5} pathOptions={{ color: '#3b82f6', weight: 2, fillColor: '#fff', fillOpacity: 1 }} />
                                                {/* Executado */}
                                                <CircleMarker
                                                    center={c.exec}
                                                    radius={realce ? 9 : 6}
                                                    pathOptions={{ color: '#fff', weight: 2, fillColor: cor, fillOpacity: 0.95 }}
                                                >
                                                    <Popup>
                                                        <p className="font-semibold border-b pb-1 mb-1.5">{c.titulo}</p>
                                                        <p className="text-xs text-gray-600 mb-0.5"><b>Operador:</b> {nome(c.vendedorId)}</p>
                                                        <p className="text-xs text-gray-600 mb-0.5"><b>Desvio:</b> <span style={{ color: cor }}>{fmtDist(c.desvioM)}</span> {c.aderente ? '(aderente)' : '(fora de rota)'}</p>
                                                        <p className="text-xs text-gray-600 mb-0.5"><b>Check-in:</b> {c.visitadoEm ? new Date(c.visitadoEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</p>
                                                        <a href={mapsLink(c.plan, c.exec)} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-primary">Ver no Google Maps →</a>
                                                    </Popup>
                                                </CircleMarker>
                                            </React.Fragment>
                                        );
                                    })}

                                    {/* Endereços planejados não visitados */}
                                    {dados.naoVisitados.filter(n => n.plan).map(n => (
                                        <CircleMarker
                                            key={n.paradaId}
                                            center={n.plan as [number, number]}
                                            radius={5}
                                            pathOptions={{ color: '#f59e0b', weight: 2, fillColor: '#fbbf24', fillOpacity: 0.85 }}
                                        >
                                            <Popup>
                                                <p className="font-semibold border-b pb-1 mb-1.5">{n.titulo}</p>
                                                <p className="text-xs text-gray-600 mb-0.5"><b>Operador:</b> {nome(n.vendedorId)}</p>
                                                <p className="text-xs text-amber-600"><b>Planejado, não visitado</b></p>
                                                <p className="text-xs text-gray-600">{new Date(n.data + 'T12:00:00').toLocaleDateString('pt-BR')}{n.rotaNome ? ` · ${n.rotaNome}` : ''}</p>
                                            </Popup>
                                        </CircleMarker>
                                    ))}
                                </MapContainer>
                            )}
                        </div>
                    </div>

                    {/* Aderência por Operador */}
                    {dados.ranking.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <Award size={15} className="text-amber-500" /> Aderência por Operador
                            </h2>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                            <th className="py-2 pr-3 font-semibold">Operador</th>
                                            <th className="py-2 pr-3 font-semibold text-right">Aderentes</th>
                                            <th className="py-2 pr-3 font-semibold text-right">Aderência</th>
                                            <th className="py-2 pr-3 font-semibold text-right">Desvio médio</th>
                                            <th className="py-2 font-semibold text-right">Não visitados</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dados.ranking.map((r, i) => (
                                            <tr key={r.id} className="border-b border-gray-50 dark:border-gray-700/50">
                                                <td className="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                                                    <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white shrink-0 ${i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-orange-400' : 'bg-gray-300 dark:bg-gray-600'}`}>{i + 1}</span>
                                                    {nome(r.id)}
                                                </td>
                                                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{r.aderentes}/{r.comparadas}</td>
                                                <td className={`py-2 pr-3 text-right font-bold ${r.taxa >= 80 ? 'text-green-600 dark:text-green-400' : r.taxa >= 50 ? 'text-amber-500' : 'text-red-500'}`}>{r.comparadas > 0 ? fmtPct(r.taxa) : '—'}</td>
                                                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{r.comparadas > 0 ? fmtDist(r.desvioMedio) : '—'}</td>
                                                <td className={`py-2 text-right font-semibold ${r.naoVisitados > 0 ? 'text-amber-500' : 'text-gray-400'}`}>{r.naoVisitados}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Tabelas: fora de rota + não visitados */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Fora de rota */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                                <AlertTriangle size={15} className="text-red-500" /> Visitas Fora de Rota ({dados.fora.length})
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Check-in a mais de {raio} m do endereço planejado</p>
                            {dados.fora.length === 0 ? (
                                <div className="text-center py-8 text-gray-400">
                                    <CheckCircle2 size={32} className="mx-auto mb-2 opacity-40 text-green-400" />
                                    <p className="text-xs">Todas as visitas mensuradas caíram dentro do raio. 👏</p>
                                </div>
                            ) : (
                                <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                                    {dados.fora.map(c => (
                                        <div
                                            key={c.paradaId}
                                            onMouseEnter={() => setDestaque(c.paradaId)}
                                            onMouseLeave={() => setDestaque(null)}
                                            className="flex items-center gap-2 p-2 rounded-xl bg-red-50/60 dark:bg-red-900/15 hover:bg-red-50 dark:hover:bg-red-900/25 transition-colors"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate">{c.titulo}</p>
                                                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                                    {c.cidade || '—'} · {nome(c.vendedorId)} · {new Date(c.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                                </p>
                                            </div>
                                            <span className="text-xs font-bold text-red-500 shrink-0">{fmtDist(c.desvioM)}</span>
                                            <a href={mapsLink(c.plan, c.exec)} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary shrink-0" title="Ver planejado × executado no Maps">
                                                <MapPin size={13} />
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Não visitados */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                                <XCircle size={15} className="text-amber-500" /> Endereços Não Visitados ({dados.naoVisitados.length})
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Paradas planejadas em rotas vencidas/concluídas sem check-in</p>
                            {dados.naoVisitados.length === 0 ? (
                                <div className="text-center py-8 text-gray-400">
                                    <CheckCircle2 size={32} className="mx-auto mb-2 opacity-40 text-green-400" />
                                    <p className="text-xs">Nenhum endereço planejado ficou para trás. 👏</p>
                                </div>
                            ) : (
                                <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
                                    {dados.naoVisitados.map(n => (
                                        <div key={n.paradaId} className="flex items-center gap-2 p-2 rounded-xl bg-amber-50/60 dark:bg-amber-900/15">
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate">{n.titulo}</p>
                                                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                                    {n.cidade || '—'} · {nome(n.vendedorId)} · {new Date(n.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}{n.rotaNome ? ` · ${n.rotaNome}` : ''}
                                                </p>
                                            </div>
                                            {n.plan && (
                                                <a href={`https://www.google.com/maps/search/?api=1&query=${n.plan[0]},${n.plan[1]}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary shrink-0" title="Ver endereço planejado">
                                                    <MapPin size={13} />
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Histórico de remanejamentos (Motoboy v3, item 2): painel do Líder Comercial */}
                    <RemanejamentosSection profiles={profiles} vendedorSel={vendedorSel} ini={ini} fim={fim} />
                </>
            )}
        </div>
    );
};

interface RemanejamentoRow {
    id: string;
    created_at: string;
    data_original: string;
    nova_data: string | null;
    motivo?: string | null;
    origem: string;
    user_id?: string | null;
    negociacoes?: { titulo?: string | null } | null;
}

const ORIGEM_CHIP: Record<string, [string, string]> = {
    operador: ['Operador', 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'],
    gestor: ['Gestor', 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'],
    alerta: ['Alerta expediente', 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'],
};

const fmtDia = (isoStr: string) => new Date(isoStr + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

/** Lista os remanejamentos de visitas do período (quem moveu, de → para, motivo e horário). */
const RemanejamentosSection: React.FC<{ profiles: Profile[]; vendedorSel: string; ini: string; fim: string }> = ({ profiles, vendedorSel, ini, fim }) => {
    const [linhas, setLinhas] = useState<RemanejamentoRow[]>([]);
    const [carregando, setCarregando] = useState(true);

    useEffect(() => {
        const fetchRemanejamentos = async () => {
            setCarregando(true);
            try {
                let query = supabase
                    .from('visitas_remanejamentos')
                    .select('id, created_at, data_original, nova_data, motivo, origem, user_id, negociacoes(titulo)')
                    .gte('created_at', `${ini}T00:00:00`)
                    .lte('created_at', `${fim}T23:59:59`)
                    .order('created_at', { ascending: false })
                    .limit(120);
                if (vendedorSel) query = query.eq('user_id', vendedorSel);
                const { data, error } = await query;
                if (error) throw error;
                setLinhas((data || []) as unknown as RemanejamentoRow[]);
            } catch (err) {
                console.error('Erro ao carregar remanejamentos:', err);
            } finally {
                setCarregando(false);
            }
        };
        fetchRemanejamentos();
    }, [vendedorSel, ini, fim]);

    const nomeOp = (id?: string | null) => profiles.find(p => String(p.id) === String(id))?.full_name?.split(' ').slice(0, 2).join(' ') || '—';

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <CalendarClock size={15} className="text-orange-500" /> Remanejamentos de Visitas ({linhas.length})
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Toda alteração de agenda dos operadores fica registrada aqui (data original → nova data, motivo e horário)</p>
            {carregando ? (
                <div className="flex justify-center py-8"><LoadingSpinner label="Carregando remanejamentos..." size="sm" /></div>
            ) : linhas.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                    <CheckCircle2 size={32} className="mx-auto mb-2 opacity-40 text-green-400" />
                    <p className="text-xs">Nenhum remanejamento no período. 👏</p>
                </div>
            ) : (
                <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
                    {linhas.map(l => {
                        const [chipLabel, chipCls] = ORIGEM_CHIP[l.origem] || [l.origem, 'bg-gray-100 text-gray-600'];
                        return (
                            <div key={l.id} className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-700">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate flex-1 min-w-0">
                                        {l.negociacoes?.titulo || 'Lead'}
                                    </p>
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${chipCls}`}>{chipLabel}</span>
                                </div>
                                <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-0.5">
                                    {fmtDia(l.data_original)} → {l.nova_data ? <b>{fmtDia(l.nova_data)}</b> : <b className="text-amber-600 dark:text-amber-400">mantida pendente</b>}
                                    <span className="text-gray-400"> · por {nomeOp(l.user_id)} · {new Date(l.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} {new Date(l.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                                </p>
                                {l.motivo && <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 italic truncate" title={l.motivo}>📝 {l.motivo}</p>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AderenciaPanel;
